import React, { useEffect, useState } from 'react';

import { isEmpty } from 'lodash';
import cx from 'classnames';

import { InteractiveElement } from '@shared/components';
import { useEffectChange, useScreenMediaQuery } from '@shared/hooks';

import {
	Button,
	Checkbox,
	ConfigProvider,
	InlineSearchSelect,
	Loader,
	Modal,
	Tooltip,
} from '@/widgets';
import { CrossIcon } from '@/assets/icons';
import themeConfig from '@/theme/themeConfig';
import { TEST_IDS } from '@/constants/testConstants';

import styles from './FiltersModal.module.scss';

const {
	FILTERS_MODAL: { CROSS_ICON_BUTTON, CLEAR_ALL_FILTERS_MODAL, APPLY_BTN },
	TRANSACTIONS: { FILTERS_TABLE },
} = TEST_IDS;

const FiltersModal = (props) => {
	const {
		isOpen,
		onClose,
		onApply,
		filterTypeConfig,
		onFilterTypeChange,
		filterTypes,
		selectedFilters = {},
		showClearAll = true,
		activeFilterType,
		selectedMIDsCount = 1,
		isStoreTerminalSearch = false,
		isSingleFilterMode = false,
		...otherProps
	} = props;

	const isMobile = useScreenMediaQuery('maxMobile');

	const [selectedFilterType, setSelectedFilterType] = useState();
	const [localSelectedFilters, setLocalSelectedFilters] =
		useState(selectedFilters);

	const _handleCloseClick = () => {
		setLocalSelectedFilters(selectedFilters);
		onClose();
	};

	const _handleApplyClick = () => {
		onApply(localSelectedFilters);
	};

	const _handleClearAllClick = () => {
		setLocalSelectedFilters({});
	};

	const _handleFilterTypeClick = (filterType) => {
		const isFilterTypeDisabled = filterTypeConfig[filterType].isDisabled?.(
			localSelectedFilters,
			selectedMIDsCount,
			isStoreTerminalSearch,
		)?.value;
		if (isFilterTypeDisabled || filterType === selectedFilterType) return;

		setSelectedFilterType(filterType);
		onFilterTypeChange(filterType, localSelectedFilters);
	};

	const _onCheckboxChange = (ev) => {
		const isSelectAllClicked = ev.target.value === 'all';
		const _updatedFilters = { ...localSelectedFilters };
		const _currentFilterOptions =
			filterTypeConfig[selectedFilterType]?.filterOptions;
		const _currentFilterIsMultiSelect =
			filterTypeConfig[selectedFilterType]?.isMultiSelect;
		if (!_updatedFilters[selectedFilterType]) {
			_updatedFilters[selectedFilterType] = [];
		} else {
			_updatedFilters[selectedFilterType] = [
				..._updatedFilters[selectedFilterType],
			]; // to break the reference
		}
		if (isSelectAllClicked) {
			if (ev.target.checked) {
				_updatedFilters[selectedFilterType] = _currentFilterOptions;
			} else {
				_updatedFilters[selectedFilterType] = [];
			}
		} else {
			const _currentFilterIndex = _updatedFilters[selectedFilterType]
				?.map((option) => option.value)
				.indexOf(ev.target.value);
			const _currentFilterObj = _currentFilterOptions.find(
				(opt) => opt.value === ev.target.value,
			);
			if (!_currentFilterIsMultiSelect) {
				_updatedFilters[selectedFilterType] =
					_currentFilterIndex === -1 ? [_currentFilterObj] : [];
			} else {
				if (_currentFilterIndex > -1) {
					_updatedFilters[selectedFilterType]?.splice(_currentFilterIndex, 1);
				} else {
					_updatedFilters[selectedFilterType]?.push(_currentFilterObj);
				}
			}
		}
		if (
			!isEmpty(localSelectedFilters[selectedFilterType]) &&
			isEmpty(_updatedFilters[selectedFilterType])
		) {
			filterTypeConfig[selectedFilterType]?.childFilters?.forEach((filter) => {
				_updatedFilters[filter] = [];
			});
		}
		if (isSingleFilterMode) {
			const filter = _updatedFilters[selectedFilterType];
			setLocalSelectedFilters({ [selectedFilterType]: filter });
		} else {
			setLocalSelectedFilters(_updatedFilters);
		}
	};

	const _onAutocompleteChange = (selectedFilters) => {
		const _updatedFilters = { ...localSelectedFilters };
		_updatedFilters[selectedFilterType] = selectedFilters;
		if (
			!isEmpty(localSelectedFilters[selectedFilterType]) &&
			isEmpty(selectedFilters)
		) {
			filterTypeConfig[selectedFilterType]?.childFilters?.forEach((filter) => {
				_updatedFilters[filter] = [];
			});
		}
		setLocalSelectedFilters(_updatedFilters);
	};

	const _renderCheckboxFilters = (filterConfig) => (
		<div className={styles.checkboxRhs}>
			{filterConfig.showSelectAll && (
				<Checkbox
					value="all"
					className={cx(styles.checkbox, styles.checkboxSelectAllFilterWrapper)}
					onChange={_onCheckboxChange}
					checked={
						localSelectedFilters[selectedFilterType]?.length ===
						filterConfig?.filterOptions?.length
					}
				>
					Select All
				</Checkbox>
			)}
			<div className={styles.checkboxGroupWrapper}>
				{filterConfig.filterOptions.map((filterItem) => {
					const _isSelected = localSelectedFilters[selectedFilterType]
						?.map((opt) => opt.value)
						?.includes(filterItem.value);
					return (
						<Checkbox
							key={filterItem.key}
							className={cx(
								styles.checkbox,
								styles.checkboxFilterWrapper,
								{ [styles.selected]: _isSelected },
								{ [styles.disabled]: filterItem.isDisabled },
							)}
							onChange={_onCheckboxChange}
							disabled={filterItem.isDisabled}
							checked={_isSelected}
							value={filterItem.value}
						>
							{filterItem.title}
						</Checkbox>
					);
				})}
			</div>
		</div>
	);

	const _renderAutoCompleteFilters = (filterConfig) => (
		<div className={styles.autoCompleteRhs}>
			<InlineSearchSelect
				{...filterConfig}
				options={filterConfig.filterOptions}
				selectedOptions={localSelectedFilters[selectedFilterType]}
				selectedFilterType={selectedFilterType}
				allAppliedFilters={localSelectedFilters}
				setSelectedOptions={_onAutocompleteChange}
				inputProps={{ bordered: false }}
				classes={{ inputContainer: styles.searchInput }}
			/>
		</div>
	);

	const _renderFilterItems = () => {
		const selectedFilterConfig = filterTypeConfig[selectedFilterType];
		if (
			!(selectedFilterType && selectedFilterConfig) ||
			selectedFilterConfig.isLoading
		) {
			return (
				<div className={styles.loaderRhs}>
					<Loader />
					<div>Loading filters...</div>
				</div>
			);
		}
		if (selectedFilterConfig.filterType === 'checkbox') {
			return _renderCheckboxFilters(selectedFilterConfig);
		}
		if (selectedFilterConfig.filterType === 'autocomplete') {
			return _renderAutoCompleteFilters(selectedFilterConfig);
		}
	};

	const modalTitle = () => (
		<div className={styles.modalTitle} data-id={FILTERS_TABLE}>
			Filters
			<Button
				onClick={_handleCloseClick}
				variant="text"
				className={styles.crossIcon}
				data-id={CROSS_ICON_BUTTON}
			>
				<CrossIcon />
			</Button>
		</div>
	);

	const modalFooter = () => (
		<div className={styles.modalFooter}>
			{showClearAll && (
				<Button
					onClick={_handleClearAllClick}
					className={styles.clearAllButton}
					variant="tertiary"
					data-id={CLEAR_ALL_FILTERS_MODAL}
				>
					Clear All Filters
				</Button>
			)}
			<Button
				data-id={APPLY_BTN}
				onClick={_handleApplyClick}
				className={styles.applyButton}
				variant="primary"
			>
				Apply
			</Button>
		</div>
	);

	useEffectChange(() => {
		if (!isOpen) return;

		if (!activeFilterType) {
			_handleFilterTypeClick(filterTypes[0].key);
		} else if (activeFilterType) {
			_handleFilterTypeClick(activeFilterType);
		}
	}, [isOpen]);

	useEffect(() => {
		setLocalSelectedFilters(selectedFilters);
	}, [selectedFilters]);

	return (
		<ConfigProvider
			theme={{
				token: {
					colorPrimary: themeConfig.primary,
				},
			}}
		>
			<Modal
				className={styles.modal}
				footer={modalFooter()}
				open={isOpen}
				title={modalTitle()}
				maskClosable
				destroyOnClose
				onCancel={_handleCloseClick}
				closable={false}
				{...otherProps}
			>
				<div className={styles.lhs}>
					{filterTypes.map((filter) => {
						const _disabledState = filterTypeConfig[filter.key]?.isDisabled?.(
							localSelectedFilters,
							selectedMIDsCount,
							isStoreTerminalSearch,
						);
						return (
							<Tooltip
								overlayClassName={styles.tooltip}
								placement={isMobile ? 'top' : 'right'}
								key={filter.key}
								title={_disabledState?.reason}
							>
								<InteractiveElement
									type="div"
									className={cx(
										styles.filterTypeWrapper,
										{ [styles.active]: filter.key === selectedFilterType },
										{ [styles.disabled]: _disabledState?.value },
									)}
									onClick={() => _handleFilterTypeClick(filter.key)}
								>
									{filter.title}
									{localSelectedFilters[filter.key]?.length > 0 && (
										<span className={styles.filterCount}>
											{localSelectedFilters[filter.key]?.length}
										</span>
									)}
								</InteractiveElement>
							</Tooltip>
						);
					})}
				</div>
				{_renderFilterItems()}
			</Modal>
		</ConfigProvider>
	);
};

export default FiltersModal;
